/**
 * Rack number pattern detection and grouping utility.
 * Detects common naming patterns (e.g., "A01"-"A10", "R1"-"R20")
 * and groups racks by prefix for easier batch operations.
 */

import type { RackSummary } from '../types/warehouseLayout';

export interface RackGroup {
  prefix: string;         // Common prefix (e.g., "A", "R", "HALL-")
  racks: RackSummary[];   // Sorted by numeric suffix
  count: number;
}

/**
 * Parse a rack number into its alphabetic prefix and numeric suffix.
 * Examples:
 *   "A01"  → { prefix: "A", number: 1 }
 *   "R12"  → { prefix: "R", number: 12 }
 *   "HALL-3" → { prefix: "HALL-", number: 3 }
 *   "42"   → { prefix: "", number: 42 }
 */
function parseRackNumber(rackNumber: string): { prefix: string; number: number } {
  // Match: optional non-digit prefix + trailing digits
  const match = rackNumber.match(/^(.*?)(\d+)$/);
  if (match) {
    return { prefix: match[1], number: parseInt(match[2], 10) };
  }
  // No numeric suffix — treat whole string as prefix
  return { prefix: rackNumber, number: 0 };
}

/**
 * Detect groups of racks with common naming prefixes.
 * Only returns groups with 2+ members (singletons are grouped as "Annet").
 * Groups and racks within are sorted for consistent display.
 */
export function detectRackGroups(racks: RackSummary[]): RackGroup[] {
  const prefixMap = new Map<string, RackSummary[]>();

  for (const rack of racks) {
    const { prefix } = parseRackNumber(rack.rackNumber);
    if (!prefixMap.has(prefix)) {
      prefixMap.set(prefix, []);
    }
    prefixMap.get(prefix)!.push(rack);
  }

  const groups: RackGroup[] = [];
  const singles: RackSummary[] = [];

  prefixMap.forEach((rackList: RackSummary[], prefix: string) => {
    if (rackList.length >= 2) {
      // Sort by numeric suffix within group
      rackList.sort((a: RackSummary, b: RackSummary) => {
        const numA = parseRackNumber(a.rackNumber).number;
        const numB = parseRackNumber(b.rackNumber).number;
        return numA - numB;
      });
      groups.push({ prefix: prefix || '#', racks: rackList, count: rackList.length });
    } else {
      singles.push(...rackList);
    }
  });

  // Sort groups by prefix alphabetically
  groups.sort((a, b) => a.prefix.localeCompare(b.prefix, undefined, { numeric: true }));

  // Add singles as "Annet" group if any
  if (singles.length > 0) {
    singles.sort((a, b) => a.rackNumber.localeCompare(b.rackNumber, undefined, { numeric: true }));
    groups.push({ prefix: 'Annet', racks: singles, count: singles.length });
  }

  return groups;
}
